import { t } from '@web/i18n';
import type { CardOriginChannel } from '@voll-intelligence/domain';

/** Date-range presets offered in the search filter bar. */
export type DatePreset = 'all' | 'today' | '7d' | '30d';

export const DATE_PRESETS: ReadonlyArray<{ key: DatePreset; label: string }> = [
  { key: 'all', label: 'Tudo' },
  { key: 'today', label: 'Hoje' },
  { key: '7d', label: '7 dias' },
  { key: '30d', label: '30 dias' },
];

/**
 * Channels offered as filter chips — only those with a live conversation
 * integration wired end-to-end (inbound + outbound) today. The domain
 * `CardOriginChannelSchema` also defines `email`/`sms`/`phone` (no integration;
 * email outbound is an explicit degraded no-op) and the non-conversational
 * `voucher`/`ticket` origins; those are omitted so operators don't filter on
 * dead options. Add a channel here once its integration ships.
 */
export const CONNECTED_CHANNELS: readonly CardOriginChannel[] = [
  'whatsapp',
  'slack',
  'teams',
  'zendesk',
  'sunshine',
  'app',
];

/** The full filter state of the card search. */
export interface CardSearchFilters {
  channels: CardOriginChannel[];
  period: DatePreset;
}

/** Initial filter state — no channels, scoped to today (the default browse window). */
export const EMPTY_FILTERS: CardSearchFilters = { channels: [], period: 'today' };

/** Footer summary of the active channel filter: "Todos os canais" | "1 canal" | "N canais". */
export function channelSummary(channels: readonly CardOriginChannel[]): string {
  if (channels.length === 0) return t('search.filter.summaryAll');
  return t('search.filter.summaryCount', { count: channels.length });
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Lower `createdAt` bound for a preset, relative to `now`. Presets are
 * open-ended (no upper bound) — the API supports `createdTo` for a future
 * custom range, but the presets here only ever set `createdFrom`.
 */
export function presetToRange(preset: DatePreset, now: Date = new Date()): { createdFrom?: Date } {
  switch (preset) {
    case 'all':
      return {};
    case 'today': {
      // UTC day boundary (deliberate, matches the indexed createdAt); near
      // local midnight this can differ from the operator's day by the TZ offset.
      const start = new Date(now);
      start.setUTCHours(0, 0, 0, 0);
      return { createdFrom: start };
    }
    case '7d':
      return { createdFrom: new Date(now.getTime() - 7 * DAY_MS) };
    case '30d':
      return { createdFrom: new Date(now.getTime() - 30 * DAY_MS) };
    default: {
      const exhaustive: never = preset;
      return exhaustive;
    }
  }
}

/** Compact localized relative label for a card's createdAt. */
export function formatRelativeDate(date: Date, now: Date = new Date()): string {
  const diffMs = now.getTime() - date.getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return t('search.relative.now');
  if (min < 60) return t('search.relative.minutes', { count: min });
  const hours = Math.floor(min / 60);
  if (hours < 24) return t('search.relative.hours', { count: hours });
  const days = Math.floor(hours / 24);
  if (days === 1) return t('search.relative.yesterday');
  if (days < 7) return t('search.relative.days', { count: days });
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}
